import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'constants/QueryKeys';
import { isBefore, subDays } from 'date-fns';
import { EnvDTO } from 'api/api';
import { Env } from 'api/Env';
import { client } from 'api/baseAxios';
import { useToastNotification } from './useToastNotification';

type TEnvDTO = (EnvDTO & { timeSaved: string }) | null;

const apiGetEnv = () => client(Env).getEnv();
const CACHE_TTL_DAYS = 7;
const CACHE_TTL_MILLIS = 1000 * 60 * 60 * 24 * CACHE_TTL_DAYS;

function isExpired(timeSaved: Date) {
  return isBefore(timeSaved, subDays(new Date(), CACHE_TTL_DAYS));
}

function readSavedEnvConfig(): TEnvDTO {
  const savedConfig = localStorage.getItem(queryKeys.ENV_CONFIG);
  if (!savedConfig) return null;

  try {
    return JSON.parse(savedConfig) as TEnvDTO;
  } catch {
    return null;
  }
}

function shouldFetchEnvConfig(parsedSavedConfig: TEnvDTO) {
  if (!parsedSavedConfig?.timeSaved) return true;
  return isExpired(new Date(parsedSavedConfig.timeSaved));
}

interface IUseEnvConfig {
  data?: EnvDTO;
  isLoading?: boolean;
  isError?: boolean;
}

export const useEnvConfig = (): IUseEnvConfig => {
  const { error } = useToastNotification();
  const parsedSavedConfig = readSavedEnvConfig();
  const shouldFetch = shouldFetchEnvConfig(parsedSavedConfig);

  const { data, isLoading, isError } = useQuery({
    queryKey: [queryKeys.ENV_CONFIG],
    queryFn: async () => {
      const config = await apiGetEnv();

      if (typeof config !== 'object' || config === null) {
        // eslint-disable-next-line no-console -- Custom error log
        console.error('Unexpected response type: Expected an object but received', config);
        error('Failed to fetch environment configuration, please refresh the page');
        throw new Error('Invalid environment configuration response');
      }

      const configToSave = {
        ...config,
        timeSaved: new Date().toString(),
      };
      localStorage.setItem(queryKeys.ENV_CONFIG, JSON.stringify(configToSave));

      return configToSave;
    },
    enabled: shouldFetch,
    retry: false,
    staleTime: CACHE_TTL_MILLIS,
    gcTime: CACHE_TTL_MILLIS,
  });

  if (!shouldFetch && parsedSavedConfig) {
    // eslint-disable-next-line unused-imports/no-unused-vars -- Extracting to omit
    const { timeSaved, ...envConfig } = parsedSavedConfig;
    return {
      data: envConfig as EnvDTO,
      isLoading: false,
      isError: false,
    };
  }

  // eslint-disable-next-line unused-imports/no-unused-vars -- Extracting to omit
  const { timeSaved, ...envConfig } = parsedSavedConfig || {};
  return {
    data: (data ?? envConfig) as EnvDTO,
    isLoading,
    isError,
  };
};
